export const RESERVED_PROFILE_SEGMENTS = [
  'commissions',
  'help',
  'connections',
  'business_center',
  'contests',
  'share',
  'downloads',
  'payout',
  'payout-frequency',
  'subscription',
  'settings',
  'announcements',
  'admin',
  'dashboard',
  'quickie-stages',
  'meme-coins',
  'nft-credit',
  'leaderboard',
  'quickcam-credits',
  'creator-upgrades',
  'lifetime-credits',
  'quick-links',
  'quicklinks',
  'sizzle-video',
  'calculator',
  'notification',
  'channels',
  'dev',
  'quickcam',
  'quickcamapp',
  'quicklink',
  'official',
  'staff',
  'support',
  'moderator',
  'administrator',
  'team',
  'billing',
  'security',
  'legal',
  'contact',
  'press',
  'about-us',
  'privacy-policy',
  'cookie-policy',
  'trust-policy',
  'terms',
  'terms-and-conditions',
  'patents',
  'investors',
  'whitepaper',
  'live-leaderboard',
  'share-wizard',
  'quick-tour',
  'quick-tour-home',
  'quickstart',
  'onboarding',
  'verify',
  'unsubscribe',
  'redirect',
  'api',
  'www',
  'mail',
  'root',
  'system',
  'null',
  'undefined',
  'login',
  'logout',
  'signin',
  'signup',
  'register',
  'account',
  'user',
  'users',
  'me',
  'home',
  'index',
  'static',
  'assets',
];

function reservedChannelKey(channelName: string): string {
  return String(channelName || '')
    .trim()
    .toLowerCase()
    .replace(/[-_]/g, '');
}

const RESERVED = new Set(RESERVED_PROFILE_SEGMENTS.map((name) => reservedChannelKey(name)));

function isReservedChannel(channel: string): boolean {
  const name = reservedChannelKey(channel);
  return Boolean(name) && RESERVED.has(name);
}

export function premiumProfileChannel(pathname: string): string | null {
  const path = String(pathname || '')
    .split('?')[0]
    .split('#')[0];
  const parts = path.split('/').filter(Boolean);
  if (parts.length !== 2 || parts[0] !== 'u') return null;
  let channel = parts[1];
  try {
    channel = decodeURIComponent(parts[1]);
  } catch {
    return null;
  }
  if (!channel || isReservedChannel(channel)) return null;
  return channel;
}

export function profilePath(channelName: string): string {
  return `/u/${encodeURIComponent(channelName)}`;
}

export function quickLinkGo(pathname: string): { channelName: string; linkId: string } | null {
  const path = String(pathname || '')
    .split('?')[0]
    .split('#')[0];
  const parts = path.split('/').filter(Boolean);
  if (parts.length !== 4 || parts[0] !== 'u' || parts[2] !== 'go') return null;
  let channel = parts[1];
  let linkId = parts[3];
  try {
    channel = decodeURIComponent(parts[1]);
    linkId = decodeURIComponent(parts[3]);
  } catch {
    return null;
  }
  if (!channel || !linkId || isReservedChannel(channel)) return null;
  return { channelName: channel, linkId };
}

export function quickLinkGoPath(channelName: string, linkId: string): string {
  return `/u/${encodeURIComponent(channelName)}/go/${encodeURIComponent(linkId)}`;
}

const APP_ENTRY_SEGMENTS = new Set([
  'terms-and-conditions',
  'terms-and-condition',
  'patentsfoot',
  'about-us',
  'live-leaderboard',
  'investors',
  'privacy-policy',
  'whitepaper',
  'cookie-policy',
  'trust-policy',
  'p-calculator-2',
  'share-wizard',
  'quick-tour',
  'quick-tour-home',
  'verify',
  'unsubscribe',
  'redirect',
  'quickstart',
  'onboarding',
  'u',
]);

const PEOPLE_LISTS = new Set(['referred', 'following', 'followers', 'favorites', 'favorited', 'gifted']);

export function profilePeoplePath(pathname: string): {
  channelName: string;
  list: 'referred' | 'following' | 'followers' | 'favorites' | 'favorited' | 'gifted';
} | null {
  const path = String(pathname || '').split('?')[0].split('#')[0];
  const parts = path.split('/').filter(Boolean);
  if (parts.length !== 3 || parts[0] !== 'u' || !PEOPLE_LISTS.has(parts[2])) return null;
  let channel = parts[1];
  try {
    channel = decodeURIComponent(parts[1]);
  } catch {
    return null;
  }
  if (!channel || isReservedChannel(channel)) return null;
  return {
    channelName: channel,
    list: parts[2] as 'referred' | 'following' | 'followers' | 'favorites' | 'favorited' | 'gifted',
  };
}

/** Landing, a referrer channel, or a public QuickLinks profile. These stay visible without a session. */
export function isPublicVisitorPath(pathname: string): boolean {
  if (profilePeoplePath(pathname)) return false;
  if (premiumProfileChannel(pathname) || quickLinkGo(pathname)) {
    return true;
  }
  const path = String(pathname || '').split('?')[0].split('#')[0];
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return true;
  if (parts.length === 1 && !APP_ENTRY_SEGMENTS.has(parts[0].toLowerCase())) return true;
  return false;
}

export function shouldRedirectToOnboarding(needsOnboarding: boolean, pathname: string): boolean {
  if (!needsOnboarding) return false;
  if (String(pathname || '').startsWith('/onboarding')) return false;
  if (premiumProfileChannel(pathname) || quickLinkGo(pathname) || profilePeoplePath(pathname)) return false;
  return true;
}

export function formatQuotedUsd(cents: number): string {
  if (!Number.isInteger(cents) || cents < 0) return '';
  return `$${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}
